import React from "react";
import Seo from "@/lib/Seo";
import { CONTACT } from "@/data/content";

export default function Terms() {
  return (
    <div>
      <Seo
        path="/terms"
        title="Terms of Site Usage"
        description="The terms governing your use of the Steady Path Funding website, brokerage services, and application process — including no guarantee of funding, arbitration, and governing law."
      />

      <section className="pt-40 pb-16 border-b border-black/10">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <div className="overline">Legal · Effective July 2026</div>
          <h1 className="mt-6 font-serif text-6xl md:text-7xl leading-[0.95]" data-testid="terms-heading">
            Terms of <em className="italic text-[color:var(--gold)]">Site Usage</em>.
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <div className="prose-editorial" data-testid="terms-body">
            <p>
              These Terms of Site Usage ("<strong>Terms</strong>") govern your
              access to and use of the Steady Path Funding website (the
              "<strong>Site</strong>") and any services we make available through
              it (the "<strong>Services</strong>"). By using the Site or
              submitting a form, you agree to these Terms. If you do not agree,
              please do not use the Site.
            </p>

            <h2>1. Who we are</h2>
            <p>
              Steady Path Funding ("<strong>Steady Path</strong>," "<strong>we</strong>,"
              "<strong>us</strong>," "<strong>our</strong>") is a commercial finance
              brokerage. We are <strong>not a lender</strong>. We introduce
              small-business borrowers to a network of independent third-party
              lenders and lending platforms. All final lending decisions,
              underwriting, pricing, and loan documents are the responsibility
              of the lender that ultimately funds your file, not Steady Path.
            </p>

            <h2>2. No guarantee of funding</h2>
            <p>
              Nothing on the Site is an offer, promise, or guarantee of funding.
              Approvals, rates, terms, and timelines shown on the Site
              (including in our blog articles, illustrative deal structures, and
              calculators) are educational examples only. Actual offers depend on
              a lender's underwriting of your specific file. You are under no
              obligation to accept any offer, and we do not charge upfront fees
              to shop your file (see also our{" "}
              <a href="/faq">FAQ</a> and any lender-specific disclosures at the
              point of offer).
            </p>

            <h2>3. Eligibility</h2>
            <p>
              You must be at least 18 years of age, a legal representative
              authorized to act on behalf of the business named in your
              submission, and located in a U.S. state where we operate. By
              submitting a form, you represent and warrant that the information
              you provide is accurate, complete, and truthful.
            </p>

            <h2>4. Illustrative content is not advice</h2>
            <p>
              Our{" "}
              <a href="/deal-structures">Deal Structures</a>, blog articles, and{" "}
              <a href="/resources">Resources</a> (including the Funding
              Estimator, MCA calculator, and SBA payment calculator) are
              educational and illustrative only. They do not constitute financial,
              legal, tax, or lending advice. Consult qualified professionals
              before making any funding decision.
            </p>

            <h2>5. Your submissions</h2>
            <p>
              When you submit information through a lead form, application, or
              newsletter form, you authorize us to share that information with
              one or more third-party lenders in our network for the purpose of
              placing your file. Please review our{" "}
              <a href="/privacy">Privacy Policy</a> for the full details of how
              your information is handled.
            </p>
            <p>
              You are responsible for the accuracy of what you submit. You agree
              not to submit information about anyone else without their
              authorization.
            </p>

            <h2>6. Communications and consent</h2>
            <p>
              By providing your contact information, you agree that we and our
              lender partners may contact you by phone (including any wireless
              number you provided), email, and — only if you opt in — SMS text
              messages, using automatic dialing or pre-recorded voice
              technology, about your inquiry or application. Consent to receive
              marketing communications is not a condition of any funding.
              Standard message and data rates may apply. Reply <strong>STOP</strong>{" "}
              to any text to opt out.
            </p>

            <h2>7. Prohibited use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Site for any unlawful, fraudulent, or abusive purpose.</li>
              <li>Submit false, misleading, or third-party information without authorization.</li>
              <li>Reverse engineer, scrape, or automate access to the Site outside of publicly permitted scraping (e.g., robots.txt / sitemap).</li>
              <li>Interfere with the Site's operation, security, or availability.</li>
              <li>Copy, republish, or resell content from the Site without our prior written permission.</li>
            </ul>

            <h2>8. Intellectual property</h2>
            <p>
              The Site's design, copy, deal structure walkthroughs, blog
              articles, logo, and other content are owned by Steady Path or its
              licensors and are protected by copyright, trademark, and other
              laws. Except for personal, non-commercial reading and sharing of
              individual articles with proper attribution, no license is
              granted to reuse the Site's content.
            </p>

            <h2>9. Third-party links and lenders</h2>
            <p>
              The Site may link to third-party lenders, articles, and tools. We
              do not control those third parties and are not responsible for
              their content, availability, terms, or privacy practices.
            </p>

            <h2>10. Disclaimers</h2>
            <p>
              THE SITE AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE"
              WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED,
              INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, NON-INFRINGEMENT, ACCURACY, OR UNINTERRUPTED OPERATION.
              We do not warrant that any specific outcome, approval, rate, or
              term will result from your use of the Site.
            </p>

            <h2>11. Limitation of liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL STEADY
              PATH, ITS AFFILIATES, OR ITS OFFICERS, EMPLOYEES, OR AGENTS BE
              LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER
              INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
              GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (A) YOUR USE
              OF OR INABILITY TO USE THE SITE OR SERVICES, OR (B) ANY OFFER,
              LOAN, OR DECISION MADE BY A THIRD-PARTY LENDER. OUR TOTAL
              CUMULATIVE LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO
              THE SITE OR SERVICES WILL NOT EXCEED ONE HUNDRED U.S. DOLLARS
              ($100).
            </p>

            <h2>12. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Steady Path,
              its affiliates, and their respective officers, employees, and
              agents from any claim, liability, damage, loss, or expense
              (including reasonable attorney's fees) arising out of or related
              to your use of the Site or Services, your submissions, or your
              violation of these Terms.
            </p>

            <h2>13. Governing law</h2>
            <p>
              These Terms are governed by the laws of the State of Illinois,
              without regard to conflict-of-laws principles. You agree that any
              dispute arising out of or related to these Terms or the Site will
              be brought exclusively in the state or federal courts located in
              Cook County, Illinois, and you consent to the personal
              jurisdiction of those courts — except where the arbitration
              provision below applies.
            </p>

            <h2>14. Arbitration and class-action waiver</h2>
            <p>
              <strong>Please read this section carefully. It affects your
              legal rights.</strong> Except for disputes involving intellectual
              property or injunctive relief, you and Steady Path agree that
              any dispute, claim, or controversy arising out of or relating to
              these Terms, the Site, or the Services will be resolved by binding
              individual arbitration administered by a reputable arbitration
              provider under its then-current rules. Arbitration will take place
              in Cook County, Illinois (or, at your election, by telephone or
              online). You and Steady Path agree that <strong>each may bring
              claims only in an individual capacity and not as a plaintiff or
              class member in any purported class or representative
              proceeding.</strong> If any part of this arbitration provision is
              found unenforceable, the remainder will remain in effect.
            </p>

            <h2>15. Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time. When we do, we will
              revise the "Effective" date at the top. Material changes will be
              highlighted on the Site. Your continued use of the Site after
              changes are posted constitutes acceptance of the updated Terms.
            </p>

            <h2>16. Contact us</h2>
            <p>
              Questions about these Terms? Email us at{" "}
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a> or call{" "}
              <a href={`tel:${CONTACT.mainPhone.replace(/[^0-9]/g, "")}`}>
                {CONTACT.mainPhone}
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
